import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { PropTypes } from 'prop-types';
import moment from 'moment';

import AlertsSection from './AlertsSection';
import StreamDetails from './StreamDetails';
import { getRollingMedians } from '../../../../util/Statistics';

// Mirrors the look-back window the maxFrameDelay alerting evaluates, so the line gives an impression of the
// value that detection works on. Deliberately a local constant: this is not meant to reproduce the alert
// condition, which additionally scopes and filters its sample server side.
const MEDIAN_WINDOW_IN_SECONDS = 5 * 60;

// viewBox height of a metric chart, in the same user units the strokes and font sizes below are given in
const BASE_CHART_HEIGHT = 24;

// the ingest charts carry the metrics that are actually read off the curve, so they get the taller panels
const INGEST_CHART_HEIGHT_FACTOR = 1.5;

// Animations
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const MetricsContainer = styled.div`
   background: #ffffff;
   border-radius: 8px;
   padding: 20px;
   margin: 16px 0;
   box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
   border: 1px solid #e5e7eb;
   animation: ${slideIn} 0.3s ease-out;
   width: 100%;
`;

const Header = styled.div`
   margin-bottom: 16px;
`;

const Title = styled.h1`
   color: #374151;
   margin: 0 0 4px 0;
   font-size: 20px;
   font-weight: 700;
`;

const Subtitle = styled.p`
   color: #6b7280;
   margin: 0;
   font-size: 14px;
`;

const StreamInfo = styled.div`
   background: #f9fafb;
   border-radius: 6px;
   padding: 12px;
   margin-bottom: 16px;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const StreamInfoRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   flex-wrap: wrap;
   gap: 12px;
`;

// the details are secondary, so their trigger sits centered below the live values
const DetailsToggleRow = styled.div`
   display: flex;
   justify-content: center;
`;

const DetailsToggle = styled.button`
   display: flex;
   align-items: center;
   gap: 6px;
   background: none;
   border: none;
   padding: 2px 6px;
   font: inherit;
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: #6b7280;
   cursor: pointer;

   &:hover {
      color: #374151;
   }

   &:focus {
      outline: none;
   }

   &:focus-visible {
      outline: 2px solid #9ca3af;
      outline-offset: 2px;
   }
`;

const ToggleChevron = styled.span`
   font-size: 10px;
   transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
   transition: transform 0.2s ease;
`;

const StreamName = styled.div`
   font-size: 14px;
   font-weight: 600;
   color: #374151;
`;

const StreamLabel = styled.span`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: 2px;
   margin-right: 3px;
`;

const StreamTime = styled.div`
   display: flex;
   gap: 16px;
   font-size: 12px;
   color: #6b7280;
`;

const TimeItem = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;

   .label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
   }

   .value {
      font-weight: 600;
      color: #374151;
   }
`;

const MetricsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, 1fr);
   gap: 12px;
`;

const MetricCard = styled.div`
   background: #f9fafb;
   border-radius: 6px;
   padding: 12px;
   border: 1px solid #e5e7eb;
   transition: all 0.2s ease;

   &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
   }
`;

const MetricHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 6px;
`;

const MetricTitle = styled.h3`
   color: #374151;
   font-size: 12px;
   font-weight: 500;
   margin: 0;
`;

const StatusIndicator = styled.div`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background-color: ${(props) => props.color};
`;

const MetricValue = styled.div`
   font-size: 20px;
   font-weight: 700;
   color: ${(props) => props.color || '#374151'};
   margin: 4px 0;
   ${(props) =>
      props.warning &&
      css`
         animation: ${pulse} 2s infinite;
      `}
`;

const MetricUnit = styled.span`
   font-size: 12px;
   color: #6b7280;
   margin-left: 4px;
   font-weight: 400;
`;

const BitrateBar = styled.div`
   width: 100%;
   height: 4px;
   background: #e5e7eb;
   border-radius: 2px;
   overflow: hidden;
   margin-top: 6px;
`;

const BitrateProgress = styled.div`
   height: 100%;
   background: ${(props) => props.color || '#10b981'};
   border-radius: 2px;
   width: ${(props) => Math.min((props.value / 5000) * 100, 100)}%;
   transition: width 0.3s ease;
`;

const SparklineSvg = styled.svg`
   width: 100%;
   height: auto;
   display: block;
   overflow: visible;
   font-family: inherit;
`;

const ChartWrapper = styled.div`
   margin-top: 10px;
   border-top: 1px solid #f0f0f0;
   padding-top: 8px;
`;

const SvgBox = styled.div`
   position: relative;
`;

const Tooltip = styled.div`
   position: absolute;
   transform: translate(-50%, -115%);
   background: #1f2937;
   color: #ffffff;
   font-size: 11px;
   font-weight: 600;
   line-height: 1.3;
   padding: 4px 7px;
   border-radius: 4px;
   white-space: nowrap;
   pointer-events: none;
   z-index: 2;
   box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
`;

const TooltipTime = styled.div`
   color: #9ca3af;
   font-size: 10px;
   font-weight: 400;
`;

const TooltipMedian = styled.div`
   color: #d1d5db;
   font-size: 10px;
   font-weight: 400;
`;

// explains the recessed second line without competing with the metric value above the chart. Sits above
// the plot and uses the wrapper's own top padding, so it does not push into the chart area below it.
const MedianLegend = styled.div`
   margin-top: -6px;
   margin-bottom: 1px;
   font-size: 9px;
   line-height: 1;
   color: #9ca3af;
   text-align: right;
`;

// "Nice numbers" for axis ticks (Heckbert) so the y-axis lands on round values like 0/1000/2000.
function niceNum(range, round) {
   const exp = Math.floor(Math.log10(range));
   const frac = range / Math.pow(10, exp);
   let nf;
   if (round) {
      nf = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
   } else {
      nf = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
   }
   return nf * Math.pow(10, exp);
}

function niceScale(min, max, maxTicks = 3) {
   if (!isFinite(min) || !isFinite(max) || min === max) {
      const base = isFinite(min) ? min : 0;
      const pad = Math.abs(base) > 0 ? Math.abs(base) * 0.1 : 1;
      return { niceMin: base - pad, niceMax: base + pad, step: pad };
   }
   const range = niceNum(max - min, false);
   const step = niceNum(range / (maxTicks - 1), true);
   return { niceMin: Math.floor(min / step) * step, niceMax: Math.ceil(max / step) * step, step };
}

// Compact line chart of the last few minutes with value (y) and time (x) axes.
// Each segment/point is colored via the metric's own green/amber/red thresholds,
// and the axis ticks let you read a point's height as a number and its timestamp.
function Sparkline({ points, metricKey, withMedian, heightFactor }) {
   const [hover, setHover] = useState(null);
   const usable = (points || []).filter((p) => typeof p.v === 'number');
   if (usable.length < 2) return null;

   const W = 220;
   // the svg scales to its container width, so a taller viewBox grows the plot area while leaving the
   // rendered size of everything drawn in user units - text, stroke widths - untouched
   const H = BASE_CHART_HEIGHT * heightFactor;
   const mLeft = 14;
   const mRight = 4;
   const mTop = 3;
   const mBottom = 6;
   const plotW = W - mLeft - mRight;
   const plotH = H - mTop - mBottom;
   const fontSize = 3.4;

   const values = usable.map((p) => p.v);
   const { niceMin, niceMax, step } = niceScale(Math.min(...values), Math.max(...values), 3);
   const domainRange = niceMax - niceMin || 1;
   const n = usable.length;

   const x = (i) => mLeft + (i * plotW) / (n - 1);
   const y = (v) => mTop + plotH - ((v - niceMin) / domainRange) * plotH;

   const axisColor = '#d1d5db';
   const gridColor = '#eef0f2';
   const textColor = '#9ca3af';
   const medianColor = '#9ca3af';
   const unit = getUnit(metricKey);

   // Rolling median over the same points the raw line is drawn from, so both series share the index based
   // x positions. It only gives an impression of the typical delay next to the spiky raw values - it is not
   // the sample the alerting decides on, that one is scoped and filtered server side.
   const medians = withMedian
      ? getRollingMedians(
           usable.map((point) => ({ x: point.t, y: point.v })),
           MEDIAN_WINDOW_IN_SECONDS,
        )
      : null;

   const yTicks = [];
   for (let t = niceMin; t <= niceMax + step / 2; t += step) yTicks.push(t);
   const timeFmt = (t) => moment.utc(t).format('HH:mm:ss');
   const bandW = plotW / Math.max(n - 1, 1);
   const hovered = hover !== null && usable[hover] ? usable[hover] : null;

   return (
      <ChartWrapper>
         {medians && <MedianLegend>{`- ${MEDIAN_WINDOW_IN_SECONDS / 60} min rolling median`}</MedianLegend>}
         <SvgBox>
            <SparklineSvg viewBox={`0 0 ${W} ${H}`}>
               {yTicks.map((val, idx) => {
                  const yy = y(val);
                  return (
                     <g key={`y-${idx}`}>
                        <line x1={mLeft} y1={yy} x2={W - mRight} y2={yy} stroke={gridColor} strokeWidth={0.25} />
                        <text x={mLeft - 3} y={yy + 1.1} textAnchor='end' fontSize={fontSize} fill={textColor}>
                           {formatValue(metricKey, val)}
                        </text>
                     </g>
                  );
               })}

               <line x1={mLeft} y1={mTop} x2={mLeft} y2={mTop + plotH} stroke={axisColor} strokeWidth={0.3} />
               <line x1={mLeft} y1={mTop + plotH} x2={W - mRight} y2={mTop + plotH} stroke={axisColor} strokeWidth={0.3} />

               {/* drawn before the raw series so it stays a recessed reference behind the actual values */}
               {medians && (
                  <polyline
                     points={medians.map((point, index) => `${x(index)},${y(point.y)}`).join(' ')}
                     fill='none'
                     stroke={medianColor}
                     strokeWidth={0.4}
                     strokeLinejoin='round'
                  />
               )}

               {usable.slice(1).map((point, index) => (
                  <line
                     key={`seg-${index}`}
                     x1={x(index)}
                     y1={y(usable[index].v)}
                     x2={x(index + 1)}
                     y2={y(point.v)}
                     stroke={getStatusColor(metricKey, point.v)}
                     strokeWidth={0.8}
                     strokeLinecap='round'
                  />
               ))}

               {usable.map((point, index) => (
                  <circle key={`pt-${index}`} cx={x(index)} cy={y(point.v)} r={0.7} fill={getStatusColor(metricKey, point.v)} />
               ))}

               {hovered && (
                  <circle
                     cx={x(hover)}
                     cy={y(hovered.v)}
                     r={1.7}
                     fill='none'
                     stroke={getStatusColor(metricKey, hovered.v)}
                     strokeWidth={0.6}
                  />
               )}

               {/* transparent per-point hit bands driving the custom hover tooltip */}
               {usable.map((point, index) => (
                  <rect
                     key={`hit-${index}`}
                     x={x(index) - bandW / 2}
                     y={mTop}
                     width={bandW}
                     height={plotH}
                     fill='transparent'
                     style={{ cursor: 'crosshair' }}
                     onMouseEnter={() => setHover(index)}
                     onMouseLeave={() => setHover(null)}
                  />
               ))}

               <text x={mLeft} y={H - 1.5} textAnchor='start' fontSize={fontSize} fill={textColor}>
                  {timeFmt(usable[0].t)}
               </text>
               <text x={mLeft + plotW / 2} y={H - 1.5} textAnchor='middle' fontSize={fontSize} fill={textColor}>
                  {timeFmt(usable[Math.floor((n - 1) / 2)].t)}
               </text>
               <text x={W - mRight} y={H - 1.5} textAnchor='end' fontSize={fontSize} fill={textColor}>
                  {timeFmt(usable[n - 1].t)}
               </text>
            </SparklineSvg>

            {hovered && (
               <Tooltip style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(hovered.v) / H) * 100}%` }}>
                  <div>
                     {formatValue(metricKey, hovered.v)}
                     {unit ? ` ${unit}` : ''}
                  </div>
                  {medians && medians[hover] && (
                     <TooltipMedian>
                        {`median ${formatValue(metricKey, medians[hover].y)}${unit ? ` ${unit}` : ''}`}
                     </TooltipMedian>
                  )}
                  <TooltipTime>{timeFmt(hovered.t)} UTC</TooltipTime>
               </Tooltip>
            )}
         </SvgBox>
      </ChartWrapper>
   );
}

Sparkline.propTypes = {
   points: PropTypes.arrayOf(PropTypes.shape({ t: PropTypes.number, v: PropTypes.number })),
   metricKey: PropTypes.string,
   withMedian: PropTypes.bool,
   heightFactor: PropTypes.number,
};

Sparkline.defaultProps = {
   heightFactor: 1,
};

// Utility functions
const getStatusColor = (type, value) => {
   switch (type) {
      case 'maxFrameDelay':
         if (value >= 1200) return '#ef4444';
         if (value >= 500) return '#f59e0b';
         return '#10b981';

      case 'streamTimeRatio':
         if (Math.abs(value - 1) >= 0.14) return '#ef4444';
         if (Math.abs(value - 1) >= 0.07) return '#f59e0b';
         return '#10b981';

      case 'latency':
         if (value >= 3) return '#ef4444';
         if (value >= 1.5) return '#f59e0b';
         return '#10b981';

      default:
         return '#10b981';
   }
};

const formatValue = (key, value) => {
   switch (key) {
      case 'bitrate':
         return Math.round(value);
      case 'maxFrameDelay':
         return Math.round(value);
      case 'streamTimeRatio':
         return value.toFixed(3);
      case 'latency':
         return value.toFixed(2);
      case 'elapsedTime':
         return formatDuration(value);
      default:
         return typeof value === 'number' ? value.toFixed(2) : value;
   }
};

const formatDuration = (seconds) => {
   const duration = moment.duration(seconds, 'seconds');
   const hours = Math.floor(duration.asHours());
   const minutes = duration.minutes();
   const secs = duration.seconds();

   if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
   }
   return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const getUnit = (key) => {
   switch (key) {
      case 'bitrate':
         return 'kbps';
      case 'maxFrameDelay':
         return 'ms';
      case 'streamTimeRatio':
         return '';
      case 'latency':
         return 's';
      case 'framerate':
         return 'fps';
      case 'elapsedTime':
         return '';
      default:
         return '';
   }
};

const getDisplayName = (key) => {
   const names = {
      bitrate: 'Bitrate',
      maxFrameDelay: 'Max Frame Delay',
      streamTimeRatio: 'Stream Time Ratio',
      elapsedTime: 'Stream Duration (HH:MM:SS)',
      latency: 'Play Latency',
      framerate: 'Framerate',
   };
   return names[key] || key;
};

const GroupLabel = styled.div`
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   font-weight: 700;
   color: #6b7280;
   margin: 0 0 8px 0;
`;

const GroupDivider = styled.div`
   border-top: 1px solid #e5e7eb;
   margin: 18px 0;
`;

const SummaryRow = styled.div`
   flex: 1;
   display: flex;
   flex-wrap: wrap;
   align-items: baseline;
   gap: 6px 14px;
   margin: 0 16px;
`;

const SummaryItem = styled.div`
   display: flex;
   align-items: baseline;
   gap: 4px;
`;

const SummaryLabel = styled.span`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: #6b7280;
`;

const SummaryValue = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${(props) => props.color || '#374151'};
`;

const SummaryUnit = styled.span`
   font-size: 10px;
   font-weight: 400;
   color: #6b7280;
   margin-left: 2px;
`;

// shown before the first ingest metrics arrive and whenever the stream has no live metrics anymore
const getInitialMetrics = () => ({
   bitrate: 0,
   maxFrameDelay: 0,
   streamTimeRatio: 0,
   elapsedTime: 0,
   timestamp: moment.utc(),
});

// Main Component
const StreamMetricsDisplay = ({ inputMetrics, stream, playerStats, resolution, history, alerting, transcodes }) => {
   const [currentTime, setCurrentTime] = useState(moment());
   const [metrics, setMetrics] = useState(getInitialMetrics);
   const [areDetailsOpen, setAreDetailsOpen] = useState(false);

   useEffect(() => {
      const timeInterval = setInterval(() => {
         setCurrentTime(moment.utc());
      }, 1000);

      return () => clearInterval(timeInterval);
   }, []);

   useEffect(() => {
      setMetrics(inputMetrics ?? getInitialMetrics());
   }, [inputMetrics]);

   const lastUpdate = metrics.timestamp;

   const renderMetricCard = ([key, value], chartHeightFactor = 1) => {
      const color = getStatusColor(key, value);
      const isWarning = color === '#f59e0b';
      const isCritical = color === '#ef4444';

      return (
         <MetricCard key={key}>
            <MetricHeader>
               <MetricTitle>{getDisplayName(key)}</MetricTitle>
               <StatusIndicator color={color} />
            </MetricHeader>
            <MetricValue color={color} warning={isWarning || isCritical}>
               {formatValue(key, value)}
               <MetricUnit>{getUnit(key)}</MetricUnit>
            </MetricValue>
            {key === 'bitrate' && (
               <BitrateBar>
                  <BitrateProgress value={value} color={color} />
               </BitrateBar>
            )}
            {history && history[key] && (
               <Sparkline
                  points={history[key]}
                  metricKey={key}
                  withMedian={key === 'maxFrameDelay'}
                  heightFactor={chartHeightFactor}
               />
            )}
         </MetricCard>
      );
   };

   const playerCards = playerStats
      ? [
           ['latency', playerStats.latency],
           ['framerate', playerStats.framerate],
        ].filter(([, value]) => typeof value === 'number')
      : [];
   const ingestCards = Object.entries(metrics).filter(([key]) => key !== 'timestamp');
   const summaryCards = [...ingestCards, ...playerCards];
   const shortLabel = (key) => (key === 'elapsedTime' ? 'Duration' : getDisplayName(key));

   const hasDetails = Boolean(transcodes);

   return (
      <MetricsContainer>
         <Header>
            <Title>Stream Metrics</Title>
            <Subtitle>Live performance monitoring</Subtitle>
         </Header>

         <StreamInfo>
            <StreamInfoRow>
               <StreamName>
                  <StreamLabel>Stream:</StreamLabel> {stream}
               </StreamName>
               <SummaryRow>
                  {summaryCards.map(([key, value]) => {
                     const color = getStatusColor(key, value);
                     const unit = getUnit(key);
                     return (
                        <SummaryItem key={`sum-${key}`}>
                           <SummaryLabel>{shortLabel(key)}</SummaryLabel>
                           <SummaryValue color={color}>
                              {formatValue(key, value)}
                              {unit ? <SummaryUnit>{unit}</SummaryUnit> : null}
                           </SummaryValue>
                        </SummaryItem>
                     );
                  })}
               </SummaryRow>
               <StreamTime>
                  <TimeItem>
                     <div className='label'>Now [UTC]</div>
                     <div className='value'>{currentTime.format('HH:mm:ss')}</div>
                  </TimeItem>
                  <TimeItem>
                     <div className='label'>Ingest Stats Updated</div>
                     <div className='value'>{lastUpdate.format('HH:mm:ss')}</div>
                  </TimeItem>
               </StreamTime>
            </StreamInfoRow>

            {hasDetails && (
               <DetailsToggleRow>
                  <DetailsToggle
                     type='button'
                     aria-expanded={areDetailsOpen}
                     onClick={() => setAreDetailsOpen(!areDetailsOpen)}
                  >
                     {areDetailsOpen ? 'Hide stream details' : 'More stream details'}
                     <ToggleChevron $open={areDetailsOpen}>{'▾'}</ToggleChevron>
                  </DetailsToggle>
               </DetailsToggleRow>
            )}

            {hasDetails && areDetailsOpen && (
               <StreamDetails
                  resolution={resolution}
                  renditions={transcodes.renditions}
                  isError={transcodes.isError}
               />
            )}
         </StreamInfo>

         {alerting && <AlertsSection {...alerting} />}

         {playerCards.length > 0 && (
            <>
               <GroupLabel>Playback (Player)</GroupLabel>
               <MetricsGrid>{playerCards.map((card) => renderMetricCard(card))}</MetricsGrid>
               <GroupDivider />
            </>
         )}

         <GroupLabel>Ingest</GroupLabel>
         <MetricsGrid>
            {ingestCards.map((card) => renderMetricCard(card, INGEST_CHART_HEIGHT_FACTOR))}
         </MetricsGrid>
      </MetricsContainer>
   );
};

StreamMetricsDisplay.propTypes = {
   inputMetrics: PropTypes.exact({
      bitrate: PropTypes.number,
      maxFrameDelay: PropTypes.number,
      streamTimeRatio: PropTypes.number,
      elapsedTime: PropTypes.number,
      timestamp: moment.isMoment,
   }),
   stream: PropTypes.string,
   playerStats: PropTypes.shape({
      latency: PropTypes.number,
      framerate: PropTypes.number,
   }),
   resolution: PropTypes.string,
   history: PropTypes.object,
   alerting: PropTypes.shape({
      alerts: PropTypes.array,
      snapshotTime: PropTypes.string,
      isError: PropTypes.bool,
   }),
   transcodes: PropTypes.shape({
      renditions: PropTypes.array,
      isError: PropTypes.bool,
   }),
};

export default StreamMetricsDisplay;
